import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  checkShouldDisplayProgressNotification,
  getLockedVideoData,
} from "./notificationsApi";

const initialState = {
  status: "idle",
  shouldShowNotification: false,
};

export const setVideoLockedNotificationAsync = createAsyncThunk(
  "notifications/setVideoLockedNotificationAsync",
  async ({ db, video }) => {
    const lockedVideo = await getLockedVideoData({ db, video });
    return lockedVideo;
  }
);

export const setProfileCompleteNotificationAsync = createAsyncThunk(
  "notifications/setProfileCompleteNotificationAsync",
  async () => {
    return true;
  }
);

export const checkShouldDisplayProgressNotificationAsync = createAsyncThunk(
  "notifications/checkShouldDisplayProgressNotificationAsync",
  async ({ learningPath, video }) => {
    const progressNotification = await checkShouldDisplayProgressNotification({
      learningPath,
      video,
    });

    return progressNotification;
  }
);

export const clearNotificationsAsync = createAsyncThunk(
  "notifications/clearNotificationsAsync",
  async () => true
);

export const setShouldShowNotificationAsync = createAsyncThunk(
  "notifications/setShouldShowNotificationAsync",
  async (shouldShowNotification) => shouldShowNotification
);

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setVideoLockedNotificationAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(
        setVideoLockedNotificationAsync.fulfilled,
        (state, { payload }) => {
          state.shouldShowNotification = true;
          state.type = "lockedVideo";
          state.prerequisite = {
            learningPath: payload.prerequisiteLearningPath,
            video: payload.prerequisiteVideo,
          };
          state.video = payload.video;
          state.status = `idle`;
        }
      )
      .addCase(checkShouldDisplayProgressNotificationAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(
        checkShouldDisplayProgressNotificationAsync.fulfilled,
        (state, { payload }) => {
          /**
           * Display the appropriate notification to the user if they've completed a section of the
           * learning path after completing the last watched video.
           */
          if (
            payload.learningPathComplete &&
            payload.learningPathComplete.onCompleteNotification
          ) {
            /**
             * If the completed learning path has an assocaited prize, set the notification type
             * value to "prize", add prize image data to local state.
             */
            if (payload.learningPathComplete.onCompleteNotification.prize) {
              const notification =
                payload.learningPathComplete.onCompleteNotification;

              state.learningPathComplete = payload.learningPathComplete;
              state.shouldShowNotification = true;                            
              state.type = "prize";              

              /**
               * If the completed learning path has the campaignComplete notification, set the
               * notification type to campaignComplete, and set state text accordingly
               */
            } else if (
              payload.learningPathComplete.onCompleteNotification
                .campaignComplete
            ) {
              const notification =
                payload.learningPathComplete.onCompleteNotification;

              state.shouldShowNotification = true;
              state.type = "campaignComplete";
              state.text = {
                heading: notification.campaignComplete.text.heading,
                paragraph: notification.campaignComplete.text.paragraph,
              };
              state.learningPathComplete = payload.learningPathComplete;

              /**
               * If the completed learning path does not have a prize assocaite with it, set the
               * notification type to "sectionComplete", add notification text to local state.
               */
            } else {
              if (payload.learningPathComplete.onCompleteNotification) {
                const notification =
                  payload.learningPathComplete.onCompleteNotification;

                state.shouldShowNotification = true;
                state.type = "sectionComplete";
                state.text = {
                  heading: notification.text.heading,
                  paragraph: notification.text.paragraph,
                };
              }
            }
          }
          state.status = `idle`;
        }
      )
      .addCase(clearNotificationsAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(clearNotificationsAsync.fulfilled, (state) => {
        state.video = undefined;
        state.type = undefined;
        state.text = undefined;
        state.prize = undefined;        
        state.lastWatched = undefined;
        state.prerequisite = undefined;
        state.shouldShowNotification = false;
        state.learningPathComplete = undefined;

        state.status = `idle`;
      })
      .addCase(setProfileCompleteNotificationAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(setProfileCompleteNotificationAsync.fulfilled, (state) => {
        state.type = `profile-complete`;
        state.text = {
          heading: "User Profile Complete",
          paragraph: "Thanks for completing your profile.",
        };
        state.status = `loading`;
      })
      .addCase(setShouldShowNotificationAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(
        setShouldShowNotificationAsync.fulfilled,
        (state, { payload }) => {
          state.shouldShowNotification = payload;
          state.status = `loading`;
        }
      );
  },
});

export default notificationsSlice.reducer;
